// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=56-60
import { Box, SimpleGrid } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { SectionHeading } from "./SectionHeading";
import {
  ContactCard,
  SupportIconAnimated,
  CommunityIconAnimated,
  BusinessIconAnimated,
  PressIconAnimated,
} from "./ContactCard";
import { FadeInOnView } from "./FadeInOnView";
import { aboutAsset } from "../aboutAssets";

const communityIcon = aboutAsset("community-icon.svg");
const pressIcon = aboutAsset("press-icon.svg");
const businessIcon = aboutAsset("business-icon.svg");
const supportIcon = aboutAsset("support-icon.svg");

const COMMUNITY_ACCENT: [number, number, number] = [139, 157, 245]; // Discord purple
const PRESS_ACCENT: [number, number, number] = [84, 199, 152]; // success-300 green
const BUSINESS_ACCENT: [number, number, number] = [255, 90, 44]; // blaze orange
const SUPPORT_ACCENT: [number, number, number] = [118, 172, 209]; // info-300 blue

const shineSlide = keyframes`
  0% { background-position: 0% 0; }
  100% { background-position: 100% 0; }
`;

function accentToRgb(a: [number, number, number]) {
  return `rgb(${a[0]},${a[1]},${a[2]})`;
}

const contacts = [
  {
    iconSrc: communityIcon,
    title: "Community",
    linkText: "Join our discord",
    linkHref: "https://discord.gg/9AZ9XfvvcG",
    isExternal: true,
    accent: COMMUNITY_ACCENT,
    iconComponent: <CommunityIconAnimated color={accentToRgb(COMMUNITY_ACCENT)} />,
  },
  {
    iconSrc: pressIcon,
    title: "Press",
    linkText: "press@globalcomix.com",
    linkHref: "mailto:press@globalcomix.com",
    accent: PRESS_ACCENT,
    iconComponent: <PressIconAnimated color={accentToRgb(PRESS_ACCENT)} />,
    extraLinks: [
      {
        text: "Press kit",
        href: "https://drive.google.com/drive/folders/1g-D3ygDpWOg0uk8HSxrHvWyKPUtR4anr",
        isExternal: true,
      },
    ],
  },
  {
    iconSrc: businessIcon,
    title: "Business Development",
    linkText: "partnerships@globalcomix.com",
    linkHref: "mailto:partnerships@globalcomix.com",
    accent: BUSINESS_ACCENT,
    iconComponent: <BusinessIconAnimated color={accentToRgb(BUSINESS_ACCENT)} />,
  },
  {
    iconSrc: supportIcon,
    title: "Support",
    linkText: "contact@globalcomix.com",
    linkHref: "mailto:contact@globalcomix.com",
    accent: SUPPORT_ACCENT,
    iconComponent: <SupportIconAnimated color={accentToRgb(SUPPORT_ACCENT)} />,
  },
];

export function GetInTouchSection() {
  return (
    <Box
      bg="charcoal.600"
      borderTopLeftRadius="40px"
      borderTopRightRadius="40px"
      boxShadow="0 -8px 40px rgba(0,0,0,0.3)"
      py="80px"
      px="24px"
      position="relative"
      sx={{
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "40px",
          borderTopLeftRadius: "40px",
          borderTopRightRadius: "40px",
          borderTop: "1px solid transparent",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.2) 35%, rgba(255,255,255,0.05) 42%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 58%, rgba(255,255,255,0.15) 65%, rgba(255,255,255,0.08) 75%, rgba(255,255,255,0.08) 100%) border-box",
          backgroundSize: "200% 100%",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1px 0 0 0",
          animation: `${shineSlide} 8s ease-in-out infinite alternate`,
          pointerEvents: "none",
        },
      }}
    >
      <Box maxW="960px" mx="auto">
        {/* Header (13:1118) */}
        <SectionHeading title="Get in touch" />

        {/* Contact cards grid (13:5474) */}
        <SimpleGrid
          columns={1}
          spacing="32px"
          mt="56px"
          sx={{
            "@media (min-width: 940px)": {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
          }}
        >
          {contacts.map((contact, index) => (
            <FadeInOnView key={contact.title} delay={index * 140}>
              <ContactCard {...contact} />
            </FadeInOnView>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
}
